import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Save, RotateCcw, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToast } from '../hooks/use-toast';
import { practiceApi } from '../services/authApi';

const PracticeProcedureEditPage = () => {
  const { procedureId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { practice, user } = useAuth();
  
  const [procedure, setProcedure] = useState(null);
  const [originalProcedure, setOriginalProcedure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedOverview, setEditedOverview] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadProcedure();
  }, [procedureId]);

  useEffect(() => {
    if (procedure && originalProcedure) {
      const originalContent = originalProcedure.overview || originalProcedure.fullText || '';
      setHasChanges(editedOverview !== originalContent);
    }
  }, [editedOverview, originalProcedure]);

  // Transform Global Procedure format to get editable content
  const getEditableContent = (proc) => {
    console.log('📝 getEditableContent called for:', proc?.name);
    console.log('📝 Has overview:', !!proc?.overview, 'Length:', proc?.overview?.length || 0);
    console.log('📝 Has purpose:', !!proc?.purpose);
    console.log('📝 Has first24Hours:', !!proc?.first24Hours, 'Length:', proc?.first24Hours?.length || 0);
    
    // Check if this is a Global Procedure (has structured fields)
    const isGlobalProcedure = proc.first24Hours || proc.painAndSwelling || proc.oralHygiene || proc.purpose;
    
    // For Global Procedures, ALWAYS build from structured fields (don't trust overview)
    if (isGlobalProcedure) {
      console.log('📝 Detected Global Procedure - building content from fields');
      let contentParts = [];
      
      if (proc.purpose) {
        contentParts.push(`Purpose:\n${proc.purpose}`);
      }
      
      if (proc.first24Hours && proc.first24Hours.length > 0) {
        const items = Array.isArray(proc.first24Hours) ? proc.first24Hours : [proc.first24Hours];
        const validItems = items.filter(item => item);
        if (validItems.length > 0) {
          contentParts.push(`\nFirst 24 Hours:\n${validItems.map(item => `• ${item}`).join('\n')}`);
        }
      }
      
      if (proc.painAndSwelling && proc.painAndSwelling.length > 0) {
        const items = Array.isArray(proc.painAndSwelling) ? proc.painAndSwelling : [proc.painAndSwelling];
        const validItems = items.filter(item => item);
        if (validItems.length > 0) {
          contentParts.push(`\nPain and Swelling:\n${validItems.map(item => `• ${item}`).join('\n')}`);
        }
      }
      
      if (proc.oralHygiene && proc.oralHygiene.length > 0) {
        const items = Array.isArray(proc.oralHygiene) ? proc.oralHygiene : [proc.oralHygiene];
        const validItems = items.filter(item => item);
        if (validItems.length > 0) {
          contentParts.push(`\nOral Hygiene:\n${validItems.map(item => `• ${item}`).join('\n')}`);
        }
      }
      
      if (proc.diet && proc.diet.length > 0) {
        const items = Array.isArray(proc.diet) ? proc.diet : [proc.diet];
        const validItems = items.filter(item => item);
        if (validItems.length > 0) {
          contentParts.push(`\nDiet:\n${validItems.map(item => `• ${item}`).join('\n')}`);
        }
      }
      
      if (proc.activity && proc.activity.length > 0) {
        const items = Array.isArray(proc.activity) ? proc.activity : [proc.activity];
        const validItems = items.filter(item => item);
        if (validItems.length > 0) {
          contentParts.push(`\nActivity:\n${validItems.map(item => `• ${item}`).join('\n')}`);
        }
      }
      
      if (proc.specialPrecautions && proc.specialPrecautions.length > 0) {
        const items = Array.isArray(proc.specialPrecautions) ? proc.specialPrecautions : [proc.specialPrecautions];
        const validItems = items.filter(item => item);
        if (validItems.length > 0) {
          contentParts.push(`\nSpecial Precautions:\n${validItems.map(item => `• ${item}`).join('\n')}`);
        }
      }
      
      if (proc.followUp && proc.followUp.length > 0) {
        const items = Array.isArray(proc.followUp) ? proc.followUp : [proc.followUp];
        const validItems = items.filter(item => item);
        if (validItems.length > 0) {
          contentParts.push(`\nFollow Up:\n${validItems.map(item => `• ${item}`).join('\n')}`);
        }
      }
      
      const result = contentParts.join('\n');
      console.log('📝 Built content length:', result.length);
      return result || 'No content available';
    }
    
    // Legacy format - use overview or fullText
    console.log('📝 Using legacy format (overview/fullText)');
    return proc.overview || proc.fullText || '';
  };

  const loadProcedure = async () => {
    try {
      setLoading(true);
      const response = await practiceApi.getPracticeProcedures();
      const procedures = response.procedures || response.data || [];
      const proc = procedures.find(p => p.id === procedureId);
      
      if (!proc) {
        throw new Error('Procedure not found');
      }
      
      // Get editable content (handles both legacy and Global Procedure formats)
      const editableContent = getEditableContent(proc);
      
      setProcedure(proc);
      setOriginalProcedure({ ...proc, overview: editableContent });
      setEditedOverview(editableContent);
    } catch (error) {
      console.error('Load procedure error:', error);
      toast({
        title: "Error",
        description: "Failed to load procedure details",
        variant: "destructive",
      });
      navigate('/procedure-library');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/practice/procedures/${procedureId}/customize`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('dentalToken')}`
          },
          body: JSON.stringify({
            overview: editedOverview
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save customization');
      }

      toast({
        title: "Success",
        description: "Your practice-specific customization has been saved",
      });
      
      setHasChanges(false);
      
      // Update original to reflect saved state
      setOriginalProcedure(prev => ({
        ...prev,
        overview: editedOverview
      }));
      
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save customization",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setEditedOverview(originalProcedure.overview || originalProcedure.fullText || '');
    toast({
      title: "Reset",
      description: "Changes have been discarded",
    });
  };

  const handleCancel = () => {
    console.log('🔙 Cancel clicked - hasChanges:', hasChanges);
    if (hasChanges) {
      const confirm = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      console.log('🔙 User confirmed:', confirm);
      if (!confirm) return;
    }
    console.log('🔙 Navigating to /procedure-library');
    try {
      navigate('/procedure-library');
      console.log('✅ Navigate called');
    } catch (error) {
      console.error('❌ Navigate error:', error);
      // Fallback to window.location
      window.location.href = '/procedure-library';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!procedure) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card>
          <CardContent className="p-6">
            <p>Procedure not found</p>
            <Button onClick={() => navigate('/procedure-library')} className="mt-4">
              Back to Library
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={handleCancel}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Library
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Edit Procedure Content
              </h1>
              <p className="text-gray-600">
                Customize this procedure for your practice. Changes will only apply to your practice.
              </p>
            </div>
            {procedure.isCustomized && (
              <Badge variant="secondary">Customized</Badge>
            )}
          </div>
        </div>

        {/* Warning Banner */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Practice-Specific Customization</p>
                <p>
                  Your changes will create a custom version for <strong>{practice?.practiceName || 'your practice'}</strong> only. 
                  The global template will remain unchanged. Other practices will not see your modifications.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{procedure.name}</span>
              {procedure.specialtyName && (
                <Badge variant="outline">{procedure.specialtyName}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Procedure Content Editor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Procedure Content
              </label>
              <Textarea
                value={editedOverview}
                onChange={(e) => setEditedOverview(e.target.value)}
                className="min-h-[600px] font-mono text-sm"
                placeholder="Enter procedure content..."
              />
              <p className="text-xs text-gray-500 mt-2">
                Tip: Use clear section headers followed by bullet points for best formatting
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="flex-1"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Customization'}
              </Button>
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={!hasChanges || saving}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              <Button
                variant="ghost"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Formatting Guide */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Formatting Guide</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <strong>Section Headers:</strong> Use clear headers like "Purpose", "First 24 Hours", "Oral Hygiene", etc.
              </div>
              <div>
                <strong>Bullet Points:</strong> Start lines with "-" or "•" for bullet points
              </div>
              <div>
                <strong>Example Format:</strong>
                <pre className="mt-2 p-3 bg-gray-100 rounded text-xs">
{`Purpose
This procedure is designed to...

First 24 Hours
- Rest and avoid strenuous activity
- Apply ice packs as directed
- Take medications as prescribed

Oral Hygiene
- Brush gently around the area
- Rinse with warm salt water`}
                </pre>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PracticeProcedureEditPage;
